import * as React from 'react'

import { useQuery } from '@apollo/client/react'
import type { ErrorLike, WatchQueryFetchPolicy } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { FragmentOf, ResultOf } from '~publish/graphql'

export const FeedsForSidebar_Feed = graphql(/* GraphQL */ `
  fragment FeedsForSidebar_Feed on Feed {
    id
    name
    lastRefreshed
    collection {
      id
    }
    connectionMetadata {
      ... on RSSFeedConnectionMetadata {
        __typename
        url
      }
      ... on NewsSearchConnectionMetadata {
        __typename
        query
      }
    }
  }
`)

export const FeedsForSidebar_Collection = graphql(
  /* GraphQL */ `
    fragment FeedsForSidebar_Collection on FeedCollection {
      id
      name
      feeds {
        id
        ...FeedsForSidebar_Feed
      }
    }
  `,
  [FeedsForSidebar_Feed],
)

export const GetFeedsForSidebar = graphql(
  /* GraphQL */ `
    query GetFeedsForSidebar($organizationId: OrganizationId!) {
      feeds(input: { organizationId: $organizationId }) {
        collection {
          id
        }
        ...FeedsForSidebar_Feed
      }
      feedCollections(first: 100, input: { organizationId: $organizationId }) {
        edges {
          node {
            id
            ...FeedsForSidebar_Collection
          }
        }
      }
    }
  `,
  [FeedsForSidebar_Collection, FeedsForSidebar_Feed],
)

type UseFeedCollectionItemResult = {
  loading: boolean
  error?: ErrorLike
  feeds: FragmentOf<typeof FeedsForSidebar_Feed>[]
  collections: ResultOf<
    typeof GetFeedsForSidebar
  >['feedCollections']['edges'][number]['node'][]
  totalFeeds: number
}

export const useFeedsForSidebar = (options: {
  organizationId: string | undefined | null
  fetchPolicy?: WatchQueryFetchPolicy
}): UseFeedCollectionItemResult => {
  const { organizationId, fetchPolicy = 'cache-and-network' } = options
  const { data, dataState, loading, error } = useQuery(GetFeedsForSidebar, {
    variables: {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error - organizationId is not always defined we skip if it is not defined
      organizationId,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy,
    skip: !organizationId,
  })

  const completeData = dataState === 'complete' ? data : undefined

  const rootFeeds = React.useMemo(
    () => completeData?.feeds?.filter((feed) => !feed?.collection) ?? [],
    [completeData?.feeds],
  )
  const collections = React.useMemo(
    () => completeData?.feedCollections?.edges?.map((edge) => edge.node) ?? [],
    [completeData?.feedCollections?.edges],
  )

  return {
    // on first load, organizationId can be delayed
    // counting that as loading to ensure we don't temporarily flash empty arrays as loaded
    loading: loading || !organizationId,
    error,
    feeds: rootFeeds,
    collections,
    totalFeeds: completeData?.feeds?.length ?? 0,
  }
}
